// P8b-B1 — Shared frontend helpers to record ONE effective weak taste signal.
//
// Capture happens ONLY at a CANONICAL successful action point (never when a
// modal/menu merely opens, never on cancel). Taste persistence is secondary
// personalization telemetry: it must NEVER block, revert, or surface a scary
// error on the primary planning mutation. All failures are swallowed + logged.
//
// The backend owns action -> signal mapping and family resolution; we only send
// { meal_id, session_id, action }.
//
// Same-family ordering (bounded correction): two DIFFERENT meal_ids can resolve
// to the SAME canonical family (parent_meal_id or meal_id) on the backend and
// therefore collide on the (user, family, session) row. To keep the final
// effective state deterministic regardless of network arrival order, we DISPATCH
// negatives first and only dispatch positives AFTER the negatives settle — so
// "kept"/"selected" (explicit positive intent) deterministically wins.

import { mealAPI } from './api';
import { getPlanningTasteSessionId } from './planningTasteSession';

// Allowed B1 weak actions (backend re-validates; this list keeps callers honest).
export var TASTE_ACTIONS = {
  KEEP: 'keep',
  SWAP_SELECTED: 'swap_selected',
  SWAP_OUT: 'swap_out',
  REMOVE: 'remove',
  REGENERATE_REPLACED: 'regenerate_replaced',
  SKIP_RECIPE: 'skip_recipe',
};

// Row statuses that are structurally protected / already resolved and therefore
// NOT a user rejection during regeneration (must never earn regenerate_replaced).
var PROTECTED_STATUSES = { completed: 1, skipped: 1 };

// Low-level poster — returns the promise (non-fatal on error) so callers can
// order dependent writes. Callers never await it for UI purposes.
function postSignal(mealId, sessionId, action) {
  if (!mealId || !sessionId || !action) return Promise.resolve();
  return mealAPI.recordTasteSignal(mealId, sessionId, action).catch(function (err) {
    console.debug('[taste] signal write failed (non-fatal)', action, err && err.message);
  });
}

function dedupe(arr) {
  var seen = {};
  var out = [];
  (arr || []).forEach(function (v) {
    if (v && !seen[v]) { seen[v] = 1; out.push(v); }
  });
  return out;
}

// First-plan taste setup (one-time Yes/No deck). Uses a DEDICATED STABLE session
// id (never the rotating planning session) so these baseline cues are a distinct
// record and are not overwritten by the user's later first real planning session.
// Non-blocking: failures are swallowed (telemetry must never break setup UX).
export var FIRST_PLAN_TASTE_SETUP_SESSION = 'first_plan_taste_setup_v1';

// Ordered write chain for setup answers. Every Yes/No/re-answer is appended in the
// EXACT order the user made it and dispatched serially, so when Back+change reuses
// the same (user, family, session) row the LATEST effective action wins
// deterministically regardless of network timing. postSignal() already swallows
// errors, so the chain never rejects and always settles (telemetry stays nonfatal).
// The UI does NOT await per answer; it only awaits waitForSetupTasteSignals() once,
// right before completion, so nothing blocks visibly between cards.
var setupSignalChain = Promise.resolve();

export function recordSetupTasteSignal(mealId, isYes) {
  if (!mealId) return Promise.resolve();
  var action = isYes ? 'taste_setup_yes' : 'taste_setup_no';
  setupSignalChain = setupSignalChain.then(function () {
    return postSignal(mealId, FIRST_PLAN_TASTE_SETUP_SESSION, action);
  });
  return setupSignalChain;
}

// Resolves once every queued setup answer has settled (never rejects). Call before
// /meals/taste-setup/complete so the final effective taste state is persisted first.
export function waitForSetupTasteSignals() {
  return setupSignalChain;
}

// Single independent weak signal (remove / skip_recipe) — no same-family race.
export function recordTasteSignal(mealId, action) {
  if (!mealId || !action) return;
  try {
    postSignal(mealId, getPlanningTasteSessionId(), action);
  } catch (e) {
    console.debug('[taste] signal skipped (non-fatal)', e && e.message);
  }
}

// Ordered swap capture: swap_out settles BEFORE swap_selected so that when the
// outgoing and incoming recipes share a canonical family the final effective
// row is deterministically POSITIVE (the selected replacement). Non-blocking;
// a failed outgoing write still attempts the incoming positive.
export function recordSwapTasteSignals(outgoingMealId, incomingMealId) {
  try {
    var sid = getPlanningTasteSessionId();
    Promise.resolve(postSignal(outgoingMealId, sid, TASTE_ACTIONS.SWAP_OUT))
      .then(function () { return postSignal(incomingMealId, sid, TASTE_ACTIONS.SWAP_SELECTED); });
  } catch (e) {
    console.debug('[taste] swap signals skipped (non-fatal)', e && e.message);
  }
}

// Ordered regeneration core: dispatch all regenerate_replaced NEGATIVES first,
// then (after they settle) the manual KEEP positives — so an explicit Keep on
// one variant deterministically wins over a same-family sibling regenerated
// away. Non-blocking. Keep ids override any literal-id collision.
export function recordOrderedRegenSignals(replacedMealIds, keepMealIds) {
  try {
    var sid = getPlanningTasteSessionId();
    var keep = dedupe(keepMealIds);
    var replaced = dedupe(replacedMealIds).filter(function (m) { return keep.indexOf(m) === -1; });
    var negatives = replaced.map(function (m) {
      return postSignal(m, sid, TASTE_ACTIONS.REGENERATE_REPLACED);
    });
    Promise.allSettled(negatives).then(function () {
      keep.forEach(function (m) { postSignal(m, sid, TASTE_ACTIONS.KEEP); });
    });
  } catch (e) {
    console.debug('[taste] regen signals skipped (non-fatal)', e && e.message);
  }
}

// Regeneration capture shared by Home + PlanningHub (daily & weekly). Given the
// plan rows in the regeneration range and the buildRegenSelection() result:
//   - manual keeps (non-linked)                       -> keep +1
//   - ACTUALLY replaceable active rows regenerated away -> regenerate_replaced -1
//   - completed/logged/skipped (protected) rows        -> ZERO (never disliked)
//   - linked structural keeps                          -> ZERO (neutral)
// Ordering (negatives-then-positives) is delegated to recordOrderedRegenSignals
// so same-family Keep-vs-regenerate conflicts resolve deterministically as Keep.
export function recordRegenTasteSignals(dayMeals, sel) {
  if (!sel) return;
  var meals = dayMeals || [];
  var linkedKeep = sel.linkedKeepIds || [];

  var planIdToMealId = {};
  meals.forEach(function (p) {
    if (p && p.plan_id) planIdToMealId[p.plan_id] = p.meal_id;
  });

  // Manual keeps: mealsToKeep plan_ids minus structurally-protected linked rows.
  var keepMealIds = (sel.mealsToKeep || [])
    .filter(function (id) { return linkedKeep.indexOf(id) === -1; })
    .map(function (pid) { return planIdToMealId[pid]; })
    .filter(Boolean);

  // Replaced negatives: only rows whose meal_id is in excludeMealIds AND whose
  // status is not protected (completed/skipped rows are cleanup/history, never a
  // regeneration rejection — this is bounded-correction #1).
  var excludeSet = {};
  (sel.excludeMealIds || []).forEach(function (m) { excludeSet[m] = 1; });
  var replacedMealIds = [];
  meals.forEach(function (row) {
    if (row && row.meal_id && excludeSet[row.meal_id] && !PROTECTED_STATUSES[row.status]) {
      replacedMealIds.push(row.meal_id);
    }
  });

  recordOrderedRegenSignals(replacedMealIds, keepMealIds);
}
